import React, { useState, useEffect, useCallback } from 'react';
import { shop as shopApi, stats as statsApi, uploads as uploadsApi } from '../utils/api';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';

export default function ShopPage({ activePlayerId }) {
  const toast = useToast();
  const [items, setItems]           = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [balance, setBalance]       = useState(0);
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState({ name:'', description:'', picture:'', cost:1 });
  const [tab, setTab]               = useState('shop');
  const [loading, setLoading]       = useState(true);
  const [uploading, setUploading]   = useState(false);
  const [redeeming, setRedeeming]   = useState(null);  // item id currently being redeemed
  const [confirmItem, setConfirmItem] = useState(null); // item awaiting confirmation

  const load = useCallback(async () => {
    if (!activePlayerId) return;
    setLoading(true);
    try {
      const [itemsData, statsData, redemptionsData] = await Promise.all([
        shopApi.list(),
        statsApi.get(activePlayerId),
        shopApi.redemptions(activePlayerId),
      ]);
      setItems(itemsData);
      setBalance(statsData.points.balance);
      setRedemptions(redemptionsData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activePlayerId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name:'', description:'', picture:'', cost:1 });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name:item.name, description:item.description||'', picture:item.picture||'', cost:item.cost });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadsApi.upload(file);
      setForm(f => ({ ...f, picture: result.url }));
      toast('Image uploaded!', 'success');
    } catch (err) {
      toast('Upload failed: ' + err.message, 'error');
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast('Item name is required', 'error'); return; }
    if (!form.cost || form.cost < 1) { toast('Cost must be at least 1 point', 'error'); return; }
    try {
      if (editing) {
        await shopApi.update(editing.id, form);
        toast('Item updated!', 'success');
      } else {
        await shopApi.create(form);
        toast('Reward added to the shop!', 'success');
      }
      setShowModal(false);
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  // Two-step redemption: first click opens confirm modal, second confirms
  const initiateRedeem = (item) => {
    if (balance < item.cost) {
      toast(`Need ${item.cost - balance} more points to redeem this.`, 'error');
      return;
    }
    setConfirmItem(item);
  };

  const confirmRedeem = async () => {
    const item = confirmItem;
    setConfirmItem(null);
    setRedeeming(item.id);
    try {
      const result = await shopApi.redeem({ player_id: activePlayerId, shop_item_id: item.id });
      setBalance(result.new_balance);
      toast(`🎉 Redeemed "${item.name}"! Balance: ${result.new_balance} pts`, 'success');
      load();
    } catch (e) {
      toast(e.message, 'error');
    } finally { setRedeeming(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this item from the shop?')) return;
    try {
      await shopApi.delete(id);
      toast('Item removed from shop.', 'info');
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  if (!activePlayerId) return <div className="empty-state"><p>Select a player</p></div>;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">🏪 Shop</h2>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <div className="points-badge">
            <span className="points-badge-star">⭐</span>
            <span className="points-badge-value">{balance}</span>
            <span className="points-badge-label">PTS</span>
          </div>
          <button className="btn btn-primary" onClick={openCreate}>+ Add Reward</button>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab${tab==='shop'    ? ' active':''}`} onClick={() => setTab('shop')}>🛒 Shop</button>
        <button className={`tab${tab==='history' ? ' active':''}`} onClick={() => setTab('history')}>
          📋 History{redemptions.length > 0 ? ` (${redemptions.length})` : ''}
        </button>
      </div>

      {tab === 'shop' && (
        loading ? (
          <div className="empty-state"><p>Loading shop...</p></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🏪</div>
            <p>The shop is empty. Add some rewards!</p>
            <br />
            <button className="btn btn-primary" onClick={openCreate}>+ Add First Reward</button>
          </div>
        ) : (
          <div className="card-grid">
            {items.map(item => {
              const canAfford = balance >= item.cost;
              const isRedeemingThis = redeeming === item.id;
              return (
                <div className={`card shop-card${!canAfford ? ' shop-card-locked' : ''}`} key={item.id}>
                  {item.picture ? (
                    <div className="shop-card-img">
                      <img src={item.picture} alt={item.name} />
                      {!canAfford && <div className="shop-card-locked-overlay">🔒</div>}
                    </div>
                  ) : (
                    <div className="shop-card-img-placeholder">
                      🎁
                      {!canAfford && <div className="shop-card-locked-overlay">🔒</div>}
                    </div>
                  )}
                  <h4 className="shop-card-name">{item.name}</h4>
                  {item.description && <p className="shop-card-desc">{item.description}</p>}
                  <div className="shop-card-cost">
                    <span>⭐ {item.cost} pts</span>
                    {!canAfford && (
                      <span className="shop-card-need">
                        Need {item.cost - balance} more
                      </span>
                    )}
                  </div>
                  <div className="btn-group">
                    <button
                      className={`btn btn-sm ${canAfford ? 'btn-gold' : ''}`}
                      onClick={() => initiateRedeem(item)}
                      disabled={!canAfford || isRedeemingThis}
                      style={{ flex: 1 }}
                    >
                      {isRedeemingThis ? '...' : canAfford ? '✨ Redeem' : 'Locked'}
                    </button>
                    <button className="btn btn-sm btn-icon" onClick={() => openEdit(item)}>✎</button>
                    <button className="btn btn-sm btn-icon btn-danger" onClick={() => remove(item.id)}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {tab === 'history' && (
        redemptions.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📋</div>
            <p>No redemptions yet. Earn points and treat yourself!</p>
          </div>
        ) : (
          <div className="card" style={{ overflowX:'auto' }}>
            <table className="data-table">
              <thead>
                <tr><th>Item</th><th>Cost</th><th>Date</th></tr>
              </thead>
              <tbody>
                {redemptions.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        {r.item_picture && (
                          <img src={r.item_picture} alt=""
                            style={{ width:32, height:32, borderRadius:4, objectFit:'cover' }} />
                        )}
                        <span style={{ fontWeight:600 }}>{r.item_name}</span>
                      </div>
                    </td>
                    <td style={{ color:'var(--accent-gold)', fontFamily:'var(--font-mono)' }}>
                      ⭐ {r.points_spent}
                    </td>
                    <td style={{ fontFamily:'var(--font-mono)', fontSize:'0.75rem', color:'var(--text-muted)' }}>
                      {new Date(r.redeemed_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Redemption Confirmation Modal */}
      {confirmItem && (
        <Modal
          title="✨ Confirm Redemption"
          onClose={() => setConfirmItem(null)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmItem(null)}>Cancel</button>
              <button className="btn btn-gold" onClick={confirmRedeem}>
                ✨ Redeem for {confirmItem.cost} pts
              </button>
            </>
          }
        >
          <div style={{ textAlign:'center', padding:'8px 0 16px' }}>
            {confirmItem.picture && (
              <img src={confirmItem.picture} alt=""
                style={{ width:80, height:80, borderRadius:8, objectFit:'cover',
                         border:'2px solid var(--accent-gold)', marginBottom:12 }} />
            )}
            {!confirmItem.picture && (
              <div style={{ fontSize:'3rem', marginBottom:12 }}>🎁</div>
            )}
            <h3 style={{ fontFamily:'var(--font-display)', color:'var(--accent-gold)',
                         fontSize:'1rem', marginBottom:8 }}>
              {confirmItem.name}
            </h3>
            {confirmItem.description && (
              <p style={{ color:'var(--text-secondary)', fontSize:'0.85rem', marginBottom:16 }}>
                {confirmItem.description}
              </p>
            )}
            <div style={{ fontFamily:'var(--font-mono)', fontSize:'0.8rem', color:'var(--text-muted)' }}>
              Cost: <span style={{ color:'var(--accent-gold)' }}>⭐ {confirmItem.cost} pts</span>
              &nbsp;·&nbsp;
              Remaining: <span style={{ color:'var(--accent-cyan)' }}>
                ⭐ {balance - confirmItem.cost} pts
              </span>
            </div>
          </div>
        </Modal>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <Modal
          title={editing ? '✎ Edit Reward' : '+ Add Reward'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>
                {editing ? 'Save Changes' : 'Add to Shop'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              placeholder="e.g. Cold Beer" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              placeholder="What is this reward?" />
          </div>
          <div className="form-group">
            <label className="form-label">Image</label>
            {form.picture && (
              <div style={{ marginBottom:10, display:'flex', alignItems:'center', gap:12 }}>
                <img src={form.picture} alt=""
                  style={{ width:80, height:60, borderRadius:4, objectFit:'cover',
                           border:'1px solid var(--border-color)' }} />
                <button className="btn btn-sm btn-danger"
                  onClick={() => setForm({...form, picture:''})}>Remove</button>
              </div>
            )}
            <label className="upload-btn">
              {uploading ? '⏳ Uploading...' : '📁 Choose Image File'}
              <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display:'none' }} />
            </label>
            <div style={{ margin:'8px 0', fontFamily:'var(--font-mono)', fontSize:'0.62rem',
                          color:'var(--text-muted)', textAlign:'center' }}>
              — or paste URL —
            </div>
            <input className="form-input" value={form.picture}
              onChange={e => setForm({...form, picture: e.target.value})}
              placeholder="https://..." />
          </div>
          <div className="form-group">
            <label className="form-label">Cost (Points) *</label>
            <input className="form-input" type="number" min="1" value={form.cost}
              onChange={e => setForm({...form, cost: parseInt(e.target.value) || 1})} />
          </div>
        </Modal>
      )}
    </div>
  );
}
